<template>
    <div v-show="store.state.currentTab === 'Palette'" class="b3-menu-content">
        <h1 class="themed-title">{{t('palette tab','Palette')}}</h1>
        <div class="ul-tab-area">
            <span
                class="ul-tab"
                :class="paletteTab === 'current' ? 'ul-tab-active' : ''"
                @click="paletteTab = 'current'"
            >
                {{t('current palette','Current Palette')}}
            </span>
            <span
                v-if="loggedIn"
                class="ul-tab"
                :class="paletteTab === 'collections' ? 'ul-tab-active' : ''"
                @click="paletteTab = 'collections'"
            >
                {{t('collections','Collections')}}
            </span>
        </div>

        <div v-show="paletteTab === 'current'">
            <div class="palette-table-holder">
                <table class="palette-table">
                    <tr class="header-row">
                        <td class="checkbox-col" @click="toggleAll">
                            <input type="checkbox" v-model="selectAll">
                        </td>
                        <td
                            class="palette-table-header"
                            @click="sort('color')"
                        >
                            {{t('color', 'Color')}}
                            <span v-if="sortedBy === 'color'">{{ reverseSort ? '▼' : '▲' }}</span>
                        </td>
                        <td
                            class="text-col palette-table-header"
                            @click="sort('description')"
                        >
                            {{t('description', 'Description')}}
                            <span v-if="sortedBy === 'description'">{{ reverseSort ? '▼' : '▲' }}</span>
                        </td>
                        <td
                            class="text-col palette-table-header"
                            @click="sort('symbol')"
                        >
                            {{t('symbol', 'Symbol')}}
                            <span v-if="sortedBy === 'symbol'">{{ reverseSort ? '▼' : '▲' }}</span>
                        </td>
                        <td
                            class="text-col palette-table-header"
                            @click="sort('bead count')"
                        >
                            {{t('bead count', 'Bead Count')}}
                            <span v-if="sortedBy === 'bead count'">{{ reverseSort ? '▼' : '▲' }}</span>
                        </td>
                        <td></td>
                        <td></td>
                    </tr>
                    <tr
                        v-for="(swatch, i) in swatchEntries" :key="i"
                    >
                        <td class="checkbox-col">
                            <input type="checkbox" v-model="swatch.checked">
                        </td>
                        <td class="bead-col">
                            <img :src="swatch.rt?.canvas?.toDataURL()">
                        </td>
                        <td class="text-col">
                            {{ swatch.description }}
                        </td>
                        <td class="text-col">
                            {{ swatch.letterCode }}
                        </td>
                        <td class="text-col">
                            {{ swatch.beadCount }}
                        </td>
                        <td>
                            <Icon
                                @click="launchEdit(swatch)"
                                class="clickable"
                                icon="edit"
                            />
                        </td>
                        <td>
                            <Icon
                                class="clickable-danger"
                                @click="deleteSwatch(swatch)"
                                v-if="swatch.beadCount === 0"
                                icon="delete"
                            />
                        </td>
                    </tr>
                </table>
            </div>

            <div class="table-button-area">
                <button @click="deleteUnused" :disabled="swatchEntries.every(s => s.beadCount > 0)">{{t('delete unused swatches','Delete unused swatches')}}</button>
                <button @click="createBeadSoup" :disabled="!somethingIsSelected">{{t('create bead soup','Create Bead Soup')}}</button>
                <button @click="assignSymbols" :disabled="!somethingIsSelected">{{t('auto assign symbols','Auto-assign symbols')}}</button>
                <button @click="matchStyles" :disabled="!somethingIsSelected">{{t('match swatches to library','Match styles to library')}}</button>
            </div>
        </div>
        <div v-show="paletteTab === 'collections'" class="palette-collection-area">
            <div v-if="editingCollection">
                <div class="subtitle">{{t('edit collection','Edit Collection')}}</div>
                <div class="add-collection-area">
                    <span class="form-element">{{t('title', 'Title')}}</span>
                    <input class="form-element" type="text" v-model="editCollectionName">
                    <span class="form-element" >{{t('color', 'Color')}}</span>
                    <input class="form-element" type="color" v-model="editCollectionColor">
                </div>
                <div class="table-button-area">
                    <button @click="completeCollectionEdit">{{t('done','Done')}}</button>
                    <button class="warning-button" @click="deleteCollection">{{t('delete','Delete')}}</button>
                </div>
            </div>
            <div v-else-if="viewingCollection">
                <div class="subtitle">
                    <Icon icon="bookmark" class="collection-bookmark-title" :style="`color: ${collectionToView.color};`"/>
                    {{ collectionToView.title }}
                </div>
                <table class="collection-details-table">
                    <tr class="header-row">
                        <td></td>
                        <td>{{t('product','Product')}}</td>
                        <td>{{t('details','Details')}}</td>
                        <td>{{t('description','Description')}}</td>
                        <td>{{t('stock','Stock')}}</td>
                        <td></td>
                        <td></td>
                    </tr>
                    <tr
                        class="collection-bead"
                        v-for="(item, i) in beadsInCollection"
                    >
                        <td class="bead-img"><img :src="getImg(item)"></td>
                        <td class="product-no">{{ item.title }}</td>
                        <td class="details">{{ item.details }}</td>
                        <td class="description">{{ item.description }}</td>
                        <td class="stock">
                            <span v-if="item.stock !== null">
                                <span class="tube-box">
                                    <span class="stock-button" :style="stockStyle(1, item.stock)"></span>
                                    <span class="stock-button" :style="stockStyle(2, item.stock)"></span>
                                    <span class="stock-button" :style="stockStyle(3, item.stock)"></span>
                                    <span class="stock-button" :style="stockStyle(4, item.stock)"></span>
                                </span>
                                <span class="tube-lid"></span>
                                <span class="tube-handle"></span>
                            </span>
                        </td>
                        <td>
                            <Icon icon="edit" class="clickable" @click="editCollectionBead(item)"/>
                        </td>
                        <td>
                            <Icon class="clickable-danger" icon="delete" @click="removeBeadFromCollection(item)" />
                        </td>
                    </tr>
                </table>
                <div>
                    <button @click="viewingCollection = false">{{t('back','Back')}}</button>
                </div>
            </div>
            <div v-else>
                <table class="collection-table">
                    <tr
                        class="collection-entry"
                        v-for="(c, i) in store.state.collections" :key="i"
                    >
                        <td
                            class="bookmark-col"
                            @click="viewCollection(c)"
                        >
                            <Icon icon="bookmark" :style="`color: ${c.color};`"/>
                        </td>
                        <td @click="viewCollection(c)">{{ c.title }}</td>
                        <td @click="viewCollection(c)">{{ c.beadIdList.length }} {{ c.beadIdList.length === 1 ? 'bead' : 'beads' }} </td>
                        <td @click="editCollection(c)" class="bookmark-col">
                            <Icon icon="edit" class="clickable"/>
                        </td>
                    </tr>
                    <tr v-if="!store.state.collections.length">
                        <td>({{t('you have no collections yet','You have no collections yet')}})</td>
                    </tr>
                </table>
                <div class="new-collection-form">
                    <div class="subtitle">{{t('new collection','New Collection')}}</div>
                    <div class="add-collection-area">
                        <span class="form-element">{{t('title','Title')}}</span>
                        <input class="form-element" type="text" v-model="newCollectionName">
                        <span class="form-element" >{{t('color', 'Color')}}</span>
                        <input class="form-element" type="color" v-model="newCollectionColor">
                        <button class="mini-button form-add-button" @click="addCollection">
                            <Icon class="nudge-down-2" icon="add"/>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { lang } from '../../myapp/src/lang.js';
import { menu } from '../../myapp/src/menu';
import { palette } from '../../myapp/src/palette';
import { modes } from '../../myapp/src/modes';
import { settings } from '../../myapp/src/settings';
import Icon from '../../components/Icon.vue';
import { beadLibrary } from '../../myapp/src/tools/beadLibrary';
import { utils } from '../../myapp/src/utils';
import { save } from '../../myapp/src/projects/save';

export default {
    data() {
        return {
            loggedIn,
            paletteTab: 'current',
            swatchEntries: [],
            selectAll: false,
            startIndex: 0,
            endIndex: 20,
            newCollectionColor: '',
            newCollectionName: '',
            collectionToEdit: {},
            collectionToView: {},
            editingCollection: false,
            viewingCollection: false,
            editCollectionColor: '',
            editCollectionName: '',
            sortedBy: null,
            reverseSort: false,
        }
    },
    components: { Icon },
    computed: {
        selectedSwatches: function () {
            return this.swatchEntries.filter(element => element.checked);
        },
        somethingIsSelected: function () {
            return this.selectedSwatches.length > 0;
        },
        beadsInCollection: function () {
            if (!this.collectionToView.beadIdList) return [];
            const result = beadLibrary
                .getStash({collection: this.collectionToView})
                .map(element => beadLibrary.convertLibraryBead(element));
            return result;
        }
    },
    methods: {
        sort(method) {

            this.reverseSort = method === this.sortedBy ?
                !this.reverseSort:
                false;

            this.sortedBy = method;

            const sortMethods = [
                {method: 'color', fn: (a, b) => a.hsl[0] < b.hsl[0] ? 1 : -1},
                {method: 'description', fn: (a, b) => a.description < b.description ? 1 : -1},
                {method: 'symbol', fn: (a, b) => a.letterCode < b.letterCode ? 1 : -1},
                {method: 'bead count', fn: (a, b) => a.beadCount < b.beadCount ? 1 : -1},
            ];

            const sortFn = sortMethods.find(element => element.method === method).fn;
            this.swatchEntries = this.swatchEntries
                .map(entry => ({...entry, hsl: utils.hexTohsl(entry.fillColor)}))
                .sort(sortFn);
            if (!this.reverseSort) this.swatchEntries = this.swatchEntries.reverse();

            this.store.mySet(
                'swatches',
                this.swatchEntries.map(e => this.store.state.swatches
                    .find(swatch => swatch.id === e.id)
                )
            );
        },
        editCollectionBead(item) {
            const swatch = {
                ...item,
                isLibraryBead: true,
                hasBeadsOnCanvas: () => false
            };
            b3.ee.emit('editSwatch', swatch);
            b3.store.mySet('showingModal', 'swatch-edit');
        },
        removeBeadFromCollection(item) {
            this.collectionToView.beadIdList = this.collectionToView.beadIdList.filter(e => e !== item.id);
            this.updateCollections(this.store.state.collections);
        },
        editCollection(c) {
            this.collectionToEdit = c;
            this.editCollectionName = c.title;
            this.editCollectionColor = c.color;
            this.editingCollection = true;
        },
        viewCollection(c) {
            this.collectionToView = c;
            this.viewingCollection = true;
        },
        addCollection() {
            const element = {
                title: this.newCollectionName,
                color: this.newCollectionColor || '#000000',
                beadIdList: []
            };
            const c = this.store.state.collections.concat(element);
            this.updateCollections(c);
        },
        updateCollections(c) {
            b3.store.mySet('collections', c);
            settings.preferences.collections = c;
            settings.update();
        },
        completeCollectionEdit() {
            this.collectionToEdit.title = this.editCollectionName;
            this.collectionToEdit.color = this.editCollectionColor;
            this.editingCollection = false;
        },
        deleteCollection() {
            const c = this.store.state.collections.filter(e => e !== this.collectionToEdit);
            this.updateCollections(c);
            this.editingCollection = false;
        },
        stockStyle(n, stock) {
            const colors = ['black', 'red', 'chocolate', '#cccc00', '#33dd33'];
            const color = n > stock ?
                'grey': colors[stock];

            return `background-color: ${color};`;
        },
        getImg(item) {
            return utils.getImg(item);
        },
        toggleAll() {
            this.swatchEntries.forEach(swatch => {
                swatch.checked = !this.selectAll;
            });
            this.selectAll = !this.selectAll;
        },
        deslectAll() {
            this.swatchEntries.forEach(swatch => {
                swatch.checked = false;
            });
            this.selectAll = false;
        },
        launchEdit(swatch) {
            const mySwatch = this.store.state.swatches.find(s => s.id === swatch.id);
            palette.select(mySwatch);
            modes.start('editSwatch');
        },
        deleteSwatch(swatch) {
            const mySwatch = this.store.state.swatches.find(s => s.id === swatch.id);
            palette.deleteSwatch(mySwatch);
            menu.resetPaletteTable();
            save.currentlyOpenFile({forceSave: true});
        },
        deleteUnused() {
            b3.renderModal(lang.get('clear unused swatches warning', 'Clear all unused swatches? This cannot be undone.'), () => {
                palette.clear();
                save.currentlyOpenFile({forceSave: true});
                b3.hideModal();
            }, null);
        },
        deleteSelectedSwatches() {
            palette.deleteSelectedSwatches(this.selectedSwatches);
            save.currentlyOpenFile({forceSave: true});
        },
        createBeadSoup() {
            menu.createBeadSoup(this.selectedSwatches);
            this.deslectAll();
        },
        assignSymbols() {
            const swatchList = this.selectedSwatches.map(s => this.store.state.swatches.find(swatch => swatch.id === s.id));
            palette.assignSymbols({swatchList, preserve: false});
            save.currentlyOpenFile({forceSave: true});
        },
        matchStyles() {
            palette.matchStyles(this.selectedSwatches);
            save.currentlyOpenFile({forceSave: true});
        },
    },
    mounted() {
        this.ee.on('renderPaletteTable', arr => {
            this.swatchEntries = arr;
        });
    }
}
</script>

<style lang="scss">
@import '../../assets/colors.scss';

.palette-table-holder {
    max-height: 500px;
    overflow-y: auto;
    .palette-table {
        border-spacing: 5px;
        .header-row {
            font-weight: bold;
        }
        .checkbox-col {
            width: 50px;
            input {
                width: 50px;
            }
        }
        .bead-col {
            width: 50px;
        }
        .text-col {
            padding: 0 10px;
        }
        .palette-table-header {
            &:hover {
                cursor: pointer;
                color: $b_theme_hover;
            }
        }
    }
}
.library-table-holder {
    max-height: calc(100vh - 300px);
    overflow-y: auto;
}
.library-table {
    border-collapse: collapse;
    .hearted {
        color: $warning;
        &:hover {
            color: $warning_hover;
        }
    }
    .item-row {
        &:hover {
            background-color: white;
            cursor: pointer;
        }
        .item-meta {
            padding: 5px;
            width: 320px;
        }
        .item-id {
            font-weight: bold;
        }
        .item-title {
            font-size: 12px;
        }
        .item-details {
            font-size: 10px;
        }
    }
}
.palette-collection-area {
    font-size: 14px;
    .subtitle {
        font-size: 18px;
        font-weight: bold;
        margin: 5px 0;
    }
    .collection-bookmark-title {
        display: inline-block;
        margin-right: 10px;
        position: relative;
        top: 5px;
    }
    .collection-details-table {
        .header-row {
            font-weight: bold;
        }
        min-width: 400px;
        border-collapse: collapse;
        margin: 10px 0;
        td {
            padding: 0 18px 0 0;
        }
    }
    .collection-table {
        min-width: 400px;
        border-collapse: collapse;
    }
    .collection-entry {
        &:hover {
            cursor: pointer;
            background-color: white;
        }
    }
    .bookmark-col {
        width: 50px;
    }
    .add-collection-area {
        position: relative;
        top: -5px;
        .form-element {
            display: inline-block;
            margin-right: 5px;
        }
        input {
            width: 100px;
        }
        input[type=color] {
            padding: 0;
            height: 22px;
            position: relative;
            top: 4px;
        }
        .form-add-button {
            position: relative;
            top: 4px;
        }
    }
    .new-collection-form {
        width: 400px;
        background-color: white;
        border-radius: 5px;
        padding: 5px 10px;
        margin: 10px 0;
    }
    .stock-button {
        display: inline-block;
        width: 10px;
        height: 10px;
        margin-right: 1px;
    }
    .tube-box {
        display: inline-block;
        border: 1px solid black;
        padding: 2px 1px 2px 2px;
    }
    .tube-lid {
        display: inline-block;
        height: 19px;
        width: 12px;
        background-color: black;
        position: relative;
        top: 5px;
    }
    .tube-handle {
        position: relative;
        top: 2px;
        left: -6px;
        display: inline-block;
        height: 10px;
        width: 8px;
        border-radius: 100%;
        border: 2px solid black;
    }


}
</style>